import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Plot from 'react-plotly.js';
import { DashboardWrapper } from 'src/shared';
import { UserSelectors } from 'src/reducers/user';
import * as api from 'src/services/api';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding: 24px;
`;
const Title = styled.h1`
  font-size: 1.5rem;
  margin-bottom: 16px;
`;
const Section = styled.section`
  margin-bottom: 24px;
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;
const TableWrap = styled.div`
  overflow-x: auto;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;
const Th = styled.th`
  text-align: left;
  padding: 8px 12px;
  border-bottom: 2px solid #e2e8f0;
`;
const Td = styled.td`
  padding: 8px 12px;
  border-bottom: 1px solid #e2e8f0;
`;
const MapTitle = styled.h2`
  font-size: 1.1rem;
  margin-bottom: 8px;
`;

export default function AdminUsers() {
  const navigate = useNavigate();
  const { profile, isAllowedAdmin } = UserSelectors();
  const [data, setData] = useState<{
    users: Array<{
      id: number;
      email: string;
      first_name: string;
      last_name: string;
      date_joined: string | null;
      signup_ip: string | null;
      consent_status: string;
      mfa_enabled: boolean;
      mfa_setup_complete: boolean;
      tier_name: string;
    }>;
    signup_map: Array<{ lat: number; lon: number; email: string; label: string }>;
    visits_map: Array<{ lat: number; lon: number; visit_count: number }>;
    visit_stats: {
      total_all_time: number;
      last_hour: number;
      last_day: number;
      last_month: number;
    };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(() => {
    setError(null);
    setLoading(true);
    api.getAdminUsersList()
      .then((res) => {
        if (res?.data) setData(res.data);
      })
      .catch((err) => {
        setError(err?.data?.message || 'Failed to load');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!profile) {
      navigate('/login');
      return;
    }
    if (isAllowedAdmin !== true) {
      navigate('/');
      return;
    }
    fetchData();
  }, [profile, isAllowedAdmin, navigate, fetchData]);

  if (!profile) return null;
  if (isAllowedAdmin !== true) return null;

  return (
    <DashboardWrapper>
      <Container>
        <Title>Admin – Users</Title>
        <p style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <Link to="/admin/users" style={{ fontWeight: 600 }}>Users</Link>
            <Link to="/admin/health">Health</Link>
            <Link to="/">Home</Link>
            <button type="button" onClick={fetchData} disabled={loading} style={{ padding: '6px 12px', cursor: loading ? 'wait' : 'pointer' }}>
              {loading ? 'Loading…' : 'Refresh'}
            </button>
        </p>
        {error && <p style={{ color: 'red' }}>{error}</p>}
        {loading && !data && <p>Loading...</p>}
        {data && (
          <>
            <Section>
              <MapTitle>Visit counts</MapTitle>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>Total (all time)</Th>
                      <Th>Last hour</Th>
                      <Th>Last day</Th>
                      <Th>Last month</Th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <Td>{data.visit_stats?.total_all_time ?? 0}</Td>
                      <Td>{data.visit_stats?.last_hour ?? 0}</Td>
                      <Td>{data.visit_stats?.last_day ?? 0}</Td>
                      <Td>{data.visit_stats?.last_month ?? 0}</Td>
                    </tr>
                  </tbody>
                </Table>
              </TableWrap>
            </Section>
            <Section>
              <MapTitle>User list</MapTitle>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>Email</Th>
                      <Th>Name</Th>
                      <Th>Sign-up IP</Th>
                      <Th>Consent</Th>
                      <Th>MFA</Th>
                      <Th>Tier</Th>
                      <Th>Joined</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.map((u) => (
                      <tr key={u.id}>
                        <Td>{u.email}</Td>
                        <Td>{[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}</Td>
                        <Td>{u.signup_ip || '—'}</Td>
                        <Td>{u.consent_status}</Td>
                        <Td>{u.mfa_enabled ? (u.mfa_setup_complete ? 'Yes' : 'Setup') : 'No'}</Td>
                        <Td>{u.tier_name}</Td>
                        <Td>{u.date_joined ? new Date(u.date_joined).toLocaleDateString() : '—'}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </Section>
            <Section>
              <MapTitle>User signups (by sign-up IP)</MapTitle>
              <Plot
                data={[{
                  type: 'scattergeo',
                  lat: data.signup_map.map((d) => d.lat),
                  lon: data.signup_map.map((d) => d.lon),
                  text: data.signup_map.map((d) => d.label),
                  mode: 'markers',
                  marker: { size: 8, color: '#3b82f6' },
                }]}
                layout={{
                  geo: { scope: 'world', showland: true },
                  margin: { t: 20, r: 20, b: 20, l: 20 },
                  height: 320,
                }}
                config={{ responsive: true }}
                style={{ width: '100%' }}
              />
            </Section>
            <Section>
              <MapTitle>All visits (by IP location, visit count)</MapTitle>
              <Plot
                data={[{
                  type: 'scattergeo',
                  lat: data.visits_map.map((d) => d.lat),
                  lon: data.visits_map.map((d) => d.lon),
                  text: data.visits_map.map((d) => `Visits: ${d.visit_count}`),
                  mode: 'markers',
                  marker: {
                    size: data.visits_map.map((d) => Math.min(8 + d.visit_count * 0.5, 30)),
                    color: data.visits_map.map((d) => d.visit_count),
                    colorscale: 'Reds',
                    showscale: true,
                  },
                }]}
                layout={{
                  geo: { scope: 'world', showland: true },
                  margin: { t: 20, r: 20, b: 20, l: 20 },
                  height: 320,
                }}
                config={{ responsive: true }}
                style={{ width: '100%' }}
              />
            </Section>
          </>
        )}
      </Container>
    </DashboardWrapper>
  );
}
